/**
 * SidebarNav.js - Sidebar de Administración con Sistema de Permisos por AdminLevel
 * 
 * REPLICADO DE NavbarG.js - Misma funcionalidad en formato vertical
 * 
 * SISTEMA DE NIVELES:
 * ==================
 * AdminLevel 1 (Básico): 
 *   - Inicio
 *   - Usuarios (gestión)
 *   - Reclamos (gestión)
 *   - Gestión (Beneficios, Talles, Resumen Capacitaciones)
 * 
 * AdminLevel 2 (Avanzado):
 *   - Todo lo de Nivel 1 +
 *   - Cargas (Capacitaciones, Diagramas, Recibos, Documentos)
 * 
 * AdminLevel 3 (Super Admin):
 *   - Todo lo de Nivel 1 y 2 +
 *   - Dashboard de Actividades (Logs, Estadísticas, KPIs)
 *   - Mapa de Calor (https://mapacalor.sibus.com.ar)
 * 
 * La configuración se maneja en NAV_ITEMS con el campo minAdminLevel
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  FaHome, FaUsers, FaExclamationTriangle, FaClipboardList, FaFileAlt, 
  FaChartLine, FaMapMarkedAlt, FaSignOutAlt, FaBars, FaTimes,
  FaChevronDown, FaChevronRight, FaFileContract
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { openHeatmapApp, validateHeatmapAccess } from '../services/heatmap.service';
import hola from '../assets/hola.jpeg';
import '../styles/SidebarNav.css';

// Configuración de navegación con permisos (replicado de NavbarG)
const NAV_ITEMS = [
  {
    id: 'inicio',
    label: 'Inicio',
    path: '/administrador',
    icon: FaHome,
    minAdminLevel: 1
  },
  {
    id: 'usuarios',
    label: 'Usuarios',
    icon: FaUsers,
    minAdminLevel: 1,
    description: 'Gestión de usuarios del sistema',
    isDropdown: true,
    children: [
      {
        id: 'usuarios-colaboradores',
        label: 'Colaboradores',
        path: '/administrador/administrar_usuarios',
        minAdminLevel: 1
      },
      {
        id: 'usuarios-administradores',
        label: 'Administradores',
        path: '/administrador/usuarios-admin',
        minAdminLevel: 3
      }
    ]
  },
  {
    id: 'reclamos',
    label: 'Reclamos',
    path: '/administrador/reclamos',
    icon: FaExclamationTriangle,
    minAdminLevel: 1,
    description: 'Gestión de reclamos'
  },
  {
    id: 'cargas',
    label: 'Cargas',
    icon: FaClipboardList,
    minAdminLevel: 2,
    description: 'Módulo de cargas (AdminLevel 2+)',
    isDropdown: true,
    children: [
      {
        id: 'cargar-capacitaciones',
        label: 'Capacitaciones',
        path: '/administrador/cargar-capacitaciones',
        minAdminLevel: 2
      },
      {
        id: 'admin-diagramas',
        label: 'Gestión de Diagramas',
        path: '/administrador/admin-diagramas',
        minAdminLevel: 2
      },
      {
        id: 'recibos',
        label: 'Recibos',
        path: '/administrador/recibos',
        minAdminLevel: 2
      },
      {
        id: 'documentos',
        label: 'Documentos',
        path: '/administrador/documentos',
        minAdminLevel: 2
      },
      {
        id: 'procedimientos',
        label: 'Procedimientos',
        path: '/home/admin/procedimientos',
        minAdminLevel: 2
      }
    ]
  },
  {
    id: 'gestion',
    label: 'Gestión',
    icon: FaFileAlt,
    minAdminLevel: 1,
    isDropdown: true,
    children: [
      {
        id: 'resumen-capacitaciones',
        label: 'Resumen Capacitaciones',
        path: '/administrador/resumen-capacitaciones',
        minAdminLevel: 1
      },
      {
        id: 'beneficios',
        label: 'Beneficios',
        path: '/administrador/beneficios',
        minAdminLevel: 1
      },
      {
        id: 'talles',
        label: 'Talles',
        path: '/administrador/talles',
        minAdminLevel: 1
      },
      {
        id: 'gestion-recibos',
        label: 'Recibos',
        path: '/administrador/gestion-recibos',
        minAdminLevel: 2
      }
    ]
  },
  {
    id: 'politicas',
    label: 'Políticas y Visión',
    path: '/administrador/politicas',
    icon: FaFileContract,
    minAdminLevel: 3,
    description: 'Gestión de políticas corporativas'
  },
  {
    id: 'actividades',
    label: 'Actividades',
    icon: FaChartLine,
    minAdminLevel: 3,
    description: 'Dashboards de actividades (Solo Super Admin)',
    isDropdown: true,
    children: [
      {
        id: 'dashboard-actividades',
        label: 'Vista General',
        path: '/administrador/dashboard-actividades',
        minAdminLevel: 3
      },
      {
        id: 'dashboard-metricas',
        label: 'Métricas Avanzadas',
        path: '/administrador/dashboard-metricas',
        minAdminLevel: 3
      }
    ]
  },
  {
    id: 'mapa-calor',
    label: 'Mapa de Calor',
    icon: FaMapMarkedAlt,
    minAdminLevel: 3,
    description: 'Mapa de calor de actividades (Solo Super Admin)',
    isExternal: true,
    externalUrl: 'https://mapacalor.sibus.com.ar'
  }
];

const SidebarNav = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  // Obtener nivel de admin del usuario
  const adminLevel = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return Number(user.AdminLevel) || 1;
    } catch {
      return 1;
    }
  }, []);

  // Obtener datos del usuario
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = user.Nombre || 'Usuario';
  const userInitials = userName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  const userLegajo = user.CdUsuario || null;
  const isLegajo43 = userLegajo === 43 || userLegajo === '43' || String(userLegajo) === '43';

  // Etiquetas de nivel
  const adminLevelLabel = {
    1: 'Básico',
    2: 'Avanzado',
    3: 'Super Admin'
  };

  const adminLevelColor = {
    1: '#ffc107',
    2: '#17a2b8',
    3: '#dc3545'
  };

  // Filtrar items según nivel de admin
  const visibleItems = useMemo(() => {
    return NAV_ITEMS.filter(item => item.minAdminLevel <= adminLevel).map(item => {
      if (item.isDropdown && item.children) {
        return {
          ...item,
          children: item.children.filter(child => child.minAdminLevel <= adminLevel)
        };
      }
      return item;
    }).filter(item => !item.isDropdown || (item.children && item.children.length > 0));
  }, [adminLevel]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('user_permisos');
    localStorage.removeItem('user_permisos_timestamp');
    navigate('/');
  };

  /**
   * Handler para abrir el mapa de calor con autenticación
   */
  const handleOpenHeatmap = (e) => {
    e.preventDefault();
    console.log('[SidebarNav] 🗺️ Click en Mapa de Calor detectado');
    
    const validation = validateHeatmapAccess();
    console.log('[SidebarNav] 🔍 Resultado de validación:', validation);
    
    if (!validation.canAccess) {
      console.warn('[SidebarNav] ⚠️ Acceso denegado:', validation.code, '-', validation.reason);
      switch (validation.code) {
        case 'NO_PERMISSION':
          toast.error('No tienes permisos para acceder al Mapa de Calor. Se requiere nivel Super Admin.');
          break;
        case 'NOT_CONFIGURED':
          toast.warning('El Mapa de Calor no está configurado. Contacta al administrador del sistema.');
          break;
        case 'NO_TOKEN':
          toast.error('No se encontró sesión activa. Por favor, inicia sesión nuevamente.');
          setTimeout(() => navigate('/'), 2000);
          break;
        case 'DISABLED':
          toast.info('El acceso al Mapa de Calor está temporalmente deshabilitado.');
          break;
        default:
          toast.error(validation.reason || 'No se puede acceder al Mapa de Calor en este momento.');
      }
      return;
    }

    console.log('[SidebarNav] ✅ Validación exitosa, abriendo mapa de calor...');
    
    openHeatmapApp({
      onError: (error) => {
        console.error('[SidebarNav] ❌ Error al abrir mapa de calor:', error);
        toast.error(error.reason || 'Error al abrir el Mapa de Calor');
      },
      onSuccess: () => {
        console.log('[SidebarNav] ✅ Mapa de calor abierto con éxito');
        toast.success('Abriendo Mapa de Calor...', { autoClose: 2000 });
      },
      useTemporaryToken: false
    });
    
    if (isMobileOpen) setIsMobileOpen(false);
  };

  // Auto-expandir menú activo
  useEffect(() => {
    visibleItems.forEach(item => {
      if (item.isDropdown && item.children) {
        const hasActiveChild = item.children.some(child => 
          location.pathname === child.path || location.pathname.startsWith(child.path + '/')
        );
        if (hasActiveChild) {
          setExpandedMenus(prev => ({ ...prev, [item.id]: true }));
        }
      }
    });
  }, [location, visibleItems]);

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar-nav ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        {/* Header */}
        <div className="sidebar-header">
          <div className="user-info">
            {isLegajo43 ? (
              <img 
                src={hola} 
                alt="Avatar" 
                className="user-avatar-img"
              />
            ) : (
              <div className="user-avatar">
                <span>{userInitials}</span>
              </div>
            )}
            {!isCollapsed && (
              <div className="user-details">
                <span className="user-name">{userName}</span>
                <span 
                  className="user-role" 
                  style={{ 
                    backgroundColor: adminLevelColor[adminLevel],
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    color: 'white'
                  }}
                >
                  Nivel {adminLevel}: {adminLevelLabel[adminLevel]}
                </span>
              </div>
            )}
          </div>
          <button 
            className="collapse-btn d-none d-md-block"
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Expandir' : 'Colapsar'}
          >
            <FaBars />
          </button>
          <button 
            className="close-btn d-md-none"
            onClick={() => setIsMobileOpen(false)}
          >
            <FaTimes />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="sidebar-menu">
          {visibleItems.map((item, index) => {
            const Icon = item.icon;
            const isFirstOfSection = index === 0 || visibleItems[index - 1]?.id === 'reclamos';

            return (
              <React.Fragment key={item.id}>
                {/* Section divider */}
                {isFirstOfSection && !isCollapsed && (
                  <span className="section-title">
                    {index === 0 ? 'ANÁLISIS' : 'GESTIÓN'}
                  </span>
                )}

                {/* Dropdown menu */}
                {item.isDropdown ? (
                  <div className="menu-item-wrapper">
                    <button 
                      className={`sidebar-menu-item dropdown-toggle ${expandedMenus[item.id] ? 'expanded' : ''}`}
                      onClick={() => toggleMenu(item.id)}
                      title={isCollapsed ? item.label : ''}
                    >
                      <Icon className="menu-icon" />
                      {!isCollapsed && (
                        <>
                          <span>{item.label}</span>
                          {expandedMenus[item.id] ? <FaChevronDown className="chevron" /> : <FaChevronRight className="chevron" />}
                        </>
                      )}
                    </button>
                    {expandedMenus[item.id] && !isCollapsed && (
                      <div className="submenu">
                        {item.children.map(child => (
                          <Link 
                            key={child.id}
                            to={child.path} 
                            className={`submenu-item ${isActive(child.path) ? 'active' : ''}`}
                            onClick={() => isMobileOpen && setIsMobileOpen(false)}
                          >
                            <span>{child.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : item.isExternal ? (
                  // Mapa de calor con autenticación
                  <button 
                    className="sidebar-menu-item"
                    onClick={handleOpenHeatmap}
                    title={isCollapsed ? item.label : item.description}
                  >
                    <Icon className="menu-icon" />
                    {!isCollapsed && <span>{item.label}</span>}
                  </button>
                ) : (
                  // Menu item normal
                  <Link 
                    to={item.path} 
                    className={`sidebar-menu-item ${isActive(item.path) && (location.pathname === item.path || item.path !== '/administrador') ? 'active' : ''}`}
                    onClick={() => isMobileOpen && setIsMobileOpen(false)}
                    title={isCollapsed ? item.label : ''}
                  >
                    <Icon className="menu-icon" />
                    {!isCollapsed && <span>{item.label}</span>}
                  </Link>
                )}
              </React.Fragment>
            );
          })}
        </nav>

        {/* Footer - Logout */}
        <div className="sidebar-footer">
          <button 
            className="sidebar-menu-item logout-btn" 
            onClick={handleLogout}
            title={isCollapsed ? 'Cerrar sesión' : ''}
          >
            <FaSignOutAlt className="menu-icon" />
            {!isCollapsed && <span>Cerrar sesión</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Toggle Button */}
      <button 
        className="sidebar-mobile-toggle d-md-none"
        onClick={() => setIsMobileOpen(true)}
      >
        <FaBars />
      </button>
    </>
  );
};

export default SidebarNav;
